import { get } from 'lodash';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import Select from 'react-select';
import ApiService from 'utills/ApiService';
import { Typography } from "@material-tailwind/react";
import { styled } from '@mui/material/styles';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import Pagination from 'components/wps/Pagination';
import moment from 'moment';
import '../dashboardEvent.scss';

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.shadows[1],
    fontSize: 11,
  },
}));

interface StatusTagProp {
  code: number;
  text: string;
}

const RenderStatusTag = (props: StatusTagProp) => {
  const { code, text } = props;

  // Canceled
  if (code === 0) {
    return (
      <div className="w-auto flex items-center justify-center px-[12px] py-[4px] bg-[#FFCFDD] rounded-[100px]">
        <span className="text-[12px] font-[500] text-[#9D0520]">{text}</span>
      </div>
    );
  }

  // Opening
  if (code === 1) {
    return (
      <div className="w-auto flex items-center justify-center px-[12px] py-[4px] bg-[#FEF9C3] rounded-[100px]">
        <span className="text-[12px] font-[500] text-[#A16207]">{text}</span>
      </div>
    );
  }

  // Sending
  if (code === 2) {
    return (
      <div className="w-auto flex items-center justify-center px-[12px] py-[4px] bg-[#F3E8FF] rounded-[100px]">
        <span className="text-[12px] font-[500] text-[#7E22D7]">{text}</span>
      </div>
    );
  }

  // Closed
  if (code === 3) {
    return (
      <div className="w-auto flex items-center justify-center px-[12px] py-[4px] bg-[#FFDAEF] rounded-[100px]">
        <span className="text-[12px] font-[500] text-[#BE189C]">{text}</span>
      </div>
    );
  }

  // Success
  if (code === 4) {
    return (
      <div className="w-auto flex items-center justify-center px-[12px] py-[4px] bg-[#DCFCE7] rounded-[100px]">
        <span className="text-[12px] font-[500] text-[#1D813F]">{text}</span>
      </div>
    );
  }

  return (
    <div className="w-auto flex items-center justify-center px-[12px] py-[4px] bg-gray-300 rounded-[100px]">
      <span className="text-[12px] font-[500] text-gray-600">{text}</span>
    </div>
  );
};

const TicketGroup = (props: any) => {
  const { ticketTab, setTicketTab, translate, eventType, ticketTypes, setTicketTypeSelected, isMobile, setLoading, token, eventID, applyDate, ticketTypeSelected, ticketPlatform, language, currentCurrency, setFilter, filter, events, shortenNumber, symbolCurrency } = props;
  const group_status_list = [
    {
      value: '',
      label: translate('all')
    },
    {
      value: '1',
      label: translate('group-opening')
    },
    {
      value: '2',
      label: translate('group-sending')
    },
    {
      value: '3',
      label: translate('group-closed')
    },
    {
      value: '4',
      label: translate('group-success')
    },
    {
      value: '0',
      label: translate('group-canceled')
    }
  ] as any;
  const [groupStatus, setGroupStatus] = useState(group_status_list[0] as any);
  const [groups, setGroups] = useState([] as any);
  const [groupName, setGroupName] = useState(null as any);
  const [groupStatistical, setGroupStatistical] = useState(null as any);
  const [ticketsBoard, setTicketBoard] = useState([] as any);
  const [tickets, setTickets] = useState([] as any);
  const [ticketID, setTicketID] = useState('');
  const [ticketPhases, setTicketPhases] = useState([] as any);
  const [ticketPhaseID, setTicketPhaseID] = useState('');
  const [pagingTickets, setPagingTickets] = useState(null as any);
  const [ticketsPage, setTicketsPage] = useState(0);

  const TABLE_HEAD = [translate('no'), translate('ticket-name'), translate("ticket-phase"), translate('price'), translate('items'), translate('gross-revenue-event'), translate('discounts'), translate('net-revenue-event')];
  const TABLE_HEAD_GROUP = [translate('no'), translate('group-name'), translate("members"), translate('paid'), translate('gross-revenue-event'), translate('discounts'), translate('net-revenue-event'), translate('expired-date'), translate('status')];

  useEffect(() => {
    if (token !== '' && events.length > 0 && ticketTab === 0 && eventID !== '') {
      fetchTickets();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, ticketTab, eventID, token]);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const result = await ApiService.get(`dashboards/orders/${eventID}/ticket-name`, token);

      if (result && result.length > 0) {
        const arr = [
          {
            value: '',
            label: translate('all-ticket')
          }
        ] as any;

        result.forEach((item: any) => {
          const obj = {
            value: get(item, `product_name_${language}`, ''),
            label: get(item, `product_name_${language}`, '')
          };

          arr.push(obj);
        });

        setTickets([...arr]);
        fetchTicketPhases();
      } else {
        setTickets([]);
        setLoading(false);
      }
    } catch (error: any) {
      toast.error(error.message);
      setLoading(false);
    }
  }

  const fetchTicketPhases = async () => {
    try {
      const result = await ApiService.get(`dashboards/orders/${eventID}/ticket-phase`, token);

      if (result && result.length > 0) {
        const arr = [
          {
            value: '',
            label: translate('all-phases')
          }
        ] as any;

        result.forEach((item: any) => {
          const obj = {
            value: item.id,
            label: get(item, `name_${language}`, '')
          };

          arr.push(obj);
        });

        setTicketPhases([...arr]);
        if (ticketsPage !== 1) {
          setTicketsPage(1);
        } else {
          setLoading(false);
        }
      } else {
        setTicketsPage(1);
        setTicketPhases([]);
        setLoading(false);
      }
    } catch (error: any) {
      toast.error(error.message);
      setLoading(false);
    }
  }

  useEffect(() => {
    if (ticketsPage > 0) {
      fetchTicketBoard();
    }
  }, [ticketsPage, ticketPhaseID, ticketID, applyDate, eventID, ticketTypeSelected, filter]);

  const fetchTicketBoard = async () => {
    setLoading(true);
    try {
      let ticket_name = ticketID;
      if (ticket_name && ticket_name !== '') {
        if (ticket_name.includes("&")) {
          ticket_name = ticket_name.replace("&", "%26");
        } else if (ticket_name.includes("+")) {
          ticket_name = ticket_name.replace("+", "%2B");
        }
      }

      const result = await ApiService.get(`dashboards/orders/${eventID}/tickets/page/${ticketsPage}?limit=10&ticket_phase=${ticketPhaseID}&ticket_name=${ticket_name}&from_date=${applyDate.start}&to_date=${applyDate.end}&order_type=${ticketTypeSelected}&currency=${currentCurrency}&ticket_platform=${ticketPlatform}`, token);

      if (result && result.tickets) {
        if (result.tickets.length > 0) {
          setPagingTickets(result.paging);
          setTicketBoard(result.tickets);
        } else {
          setPagingTickets(null);
          setTicketBoard([]);
        }
      }
      setLoading(false);
    } catch (error: any) {
      toast.error(error.message);
      setLoading(false);
    }

    setFilter(false);
  }

  useEffect(() => {
    if (token !== '' && eventID !== '' && ticketTab === 1) {
      fetchGroupName();
    }
  }, [eventID, ticketTab, language, translate, token]);

  const fetchGroupName = async () => {
    setLoading(true);
    try {
      const result = await ApiService.get(`dashboards/orders/groups-info/${eventID}/name`, token);

      if (result && result.length > 0) {
        const arr = [
          {
            value: '',
            label: translate('all')
          }
        ] as any;

        result.forEach((item: any) => {
          const obj = {
            value: get(item, `name_${language}`, ''),
            label: get(item, `name_${language}`, '')
          };

          arr.push(obj)
        });

        setGroups([...arr]);
        setGroupName(arr[0]);
      } else {
        setLoading(false);
      }

    } catch (error: any) {
      toast.error(error.message);
      setLoading(false);
    }
  }

  useEffect(() => {
    if (token !== '' && groupName) {
      fetchGroupStats();
    }
  }, [groupName, groupStatus, eventID, applyDate, token]);

  const fetchGroupStats = async () => {
    setLoading(true);
    try {
      const result = await ApiService.get(`dashboards/orders/groups-info/${eventID}/statistical?group_status=${groupStatus.value}&q=${groupName.value}&from_date=${applyDate.start}&to_date=${applyDate.end}&currency=${currentCurrency}`, token);

      if (result) {
        setGroupStatistical(result);
      }

    } catch (error: any) {
      toast.error(error.message);
    }
    setLoading(false);
  }

  const handleExportGroup = async () => {
    setLoading(true);

    const wsCurrency = localStorage.getItem('wsCurrency');

    const currentCurrency = (wsCurrency && wsCurrency !== '') ? wsCurrency : 'vnd';

    try {
      const group = groupName;
      let group_name = group ? group?.value : '';

      const result = await ApiService.get(`exports/group?group_name=${group_name}&group_status=${groupStatus.value}&from_date=${applyDate.start}&to_date=${applyDate.end}&event_id=${eventID}&currency=${currentCurrency}`, token);

      if (result) {
        toast.success(translate('export-success'));
      }
    } catch (error: any) {
      toast.error(error.message);
    }

    setLoading(false);
  }

  const handleExportTicket = async () => {
    setLoading(true);
    const wsCurrency = localStorage.getItem('wsCurrency');

    const currentCurrency = (wsCurrency && wsCurrency !== '') ? wsCurrency : 'vnd';

    try {
      const result = await ApiService.get(`exports/ticket?ticket_phase=${ticketPhaseID}&ticket_name=${ticketID}&from_date=${applyDate.start}&to_date=${applyDate.end}&event_id=${eventID}&order_type=${ticketTypeSelected}&currency=${currentCurrency}&ticket_platform=${ticketPlatform}`, token);

      if (result) {
        toast.success(translate('export-success'));
      }
    } catch (error: any) {
      toast.error(error.message);
    }
    setLoading(false);
  }

  return (
    <div className={`w-full my-[12px] bg-white ${!isMobile && 'rounded-[8px] p-[16px]'}`}>
      <div className={`w-full h-[40px] border-b border-[#e9ecff] flex items-center ${isMobile && 'grid grid-cols-2'}`}>
        <div
          className={`${isMobile ? 'w-full' : 'w-[130px]'} h-[40px] text-16 font-500 flex items-center justify-center cursor-pointer ${(ticketTab === 0) ? 'text-[#1814f3] border-b-[2px] border-[#1814f3]' : 'text-[#718ebf]'}`}
          onClick={() => setTicketTab(0)}>{translate('ticket')}</div>
        <div
          className={`${isMobile ? 'w-full' : 'w-[130px]'} h-[40px] text-16 font-500 flex items-center justify-center cursor-pointer ${(ticketTab === 1) ? 'text-[#1814f3] border-b-[2px] border-[#1814f3]' : 'text-[#718ebf]'}`}
          onClick={() => setTicketTab(1)}>{translate('group')}</div>
      </div>

      {
        ticketTab === 0 &&
        <div className={isMobile ? `p-[16px] grid grid-cols-1 gap-[12px]` : `grid gap-[12px] my-[16px] ${eventType === 'sports' ? 'grid-cols-2' : 'grid-cols-3'}`}>
          {
            tickets.length > 0 &&
            <div className="w-full">
              <div className="text-14 font-500 mb-[8px]">{translate('ticket-name')}</div>

              <Select
                styles={{
                  input: (base) => ({
                    ...base,
                    "input:focus": {
                      boxShadow: "none",
                    },
                  })
                }}
                defaultValue=""
                name="tickets"
                options={tickets}
                className="text-14"
                classNamePrefix="select"
                placeholder={tickets[0].label}
                onChange={(e: any) => {
                  setTicketID(e.value);
                  setTicketsPage(1);
                }} />
            </div>
          }

          {
            ticketPhases.length > 0 &&
            <div className="w-full">
              <div className="text-14 font-500 mb-[8px]">{translate('ticket-phase')}</div>

              <Select
                styles={{
                  input: (base) => ({
                    ...base,
                    "input:focus": {
                      boxShadow: "none",
                    },
                  })
                }}
                defaultValue=""
                name="ticket phases"
                options={ticketPhases}
                className="filter-item__select"
                classNamePrefix="select"
                placeholder={ticketPhases[0].label}
                onChange={(e: any) => {
                  setTicketPhaseID(e.value);
                  setTicketsPage(1);
                }} />
            </div>
          }

          {
            eventType === 'sports' &&
            <div className="w-full">
              <div className="text-14 font-500 mb-[8px]">{translate('ticket-type')}</div>

              <Select
                styles={{
                  input: (base) => ({
                    ...base,
                    "input:focus": {
                      boxShadow: "none",
                    },
                  })
                }}
                defaultValue=""
                name="ticket phases"
                options={ticketTypes}
                className="filter-item__select"
                classNamePrefix="select"
                placeholder={ticketTypes[0].label}
                onChange={(e: any) => {
                  setTicketTypeSelected(e.value);
                  setTicketsPage(1);
                }} />
            </div>
          }

          <div className={`${isMobile ? 'w-[100%]' : 'w-[165px] mt-[29px]'} h-[38px] rounded-[4px] border-[1px] border-[#A6A1FD] flex items-center justify-center cursor-pointer text-[#081774] btn-export-group px-[20px]`} onClick={handleExportTicket}>
            <img src="/images/icons/icon-download.svg" className="mr-[4px]" alt="icon-download" />

            <div className="text-[14px]">{translate('export-report')}</div>
          </div>

        </div>
      }

      {
        (ticketTab === 1) &&
        <div className="w-full p-[16px]">
          <div className={`${isMobile ? 'grid grid-cols-2' : 'grid grid-cols-4'} w-full gap-[12px]`}>
            <div className="w-full p-[11px] border border-[#c5ccd2] flex gap-[8px] rounded-[4px]">
              <div className="size-[40px] rounded-full flex items-center justify-center bg-[#e7edff]">
                <img src="/images/icons/user-group-event.svg" alt="icon-total-group" />
              </div>

              <div className="flex-1">
                <div className={`${isMobile ? 'text-12' : 'text-14'} w-full h-[42px] mb-[8px] text-[#718ebf]`}>{translate('total-group')}</div>
                {
                  groupStatistical && groupStatistical.total_group ?
                    <div className={`${isMobile ? 'text-12' : 'text-20'} font-700`}>{groupStatistical.total_group}</div>
                    :
                    <div className={`${isMobile ? 'text-12' : 'text-20'} font-700`}>0</div>
                }
              </div>
            </div>

            <div className="w-full p-[11px] border border-[#c5ccd2] flex gap-[8px] rounded-[4px]">
              <div className="size-[40px] rounded-full flex items-center justify-center bg-[#def6ff]">
                <img src="/images/icons/group-payment.svg" alt="icon-total-group" />
              </div>

              <div className="flex-1">
                <div className={`${isMobile ? 'text-12' : 'text-14'} w-full h-[42px] mb-[8px] text-[#718ebf]`}>{translate('total-group-payment')}</div>
                {
                  groupStatistical && groupStatistical.total_paid_group ?
                    <div className={`${isMobile ? 'text-12' : 'text-20'} font-700`}>{groupStatistical.total_paid_group}</div>
                    :
                    <div className={`${isMobile ? 'text-12' : 'text-20'} font-700`}>0</div>
                }
              </div>
            </div>

            <div className="w-full p-[11px] border border-[#c5ccd2] flex gap-[8px] rounded-[4px]">
              <div className="size-[40px] rounded-full flex items-center justify-center bg-[#ddfdcd]">
                <img src="/images/icons/member-event.svg" alt="icon-total-group" />
              </div>

              <div className="flex-1">
                <div className={`${isMobile ? 'text-12' : 'text-14'} w-full h-[42px] mb-[8px] text-[#718ebf]`}>{translate('total-member')}</div>
                {
                  groupStatistical && groupStatistical.total_member ?
                    <div className={`${isMobile ? 'text-12' : 'text-20'} font-700`}>{groupStatistical.total_member}</div>
                    :
                    <div className={`${isMobile ? 'text-12' : 'text-20'} font-700`}>0</div>
                }
              </div>
            </div>

            <div className="w-full p-[11px] border border-[#c5ccd2] flex gap-[8px] rounded-[4px]">
              <div className="size-[40px] rounded-full flex items-center justify-center bg-[#ddfdcd]">
                <img src="/images/icons/member-payment-event.svg" alt="icon-total-group" />
              </div>

              <div className="flex-1">
                <div className={`${isMobile ? 'text-12' : 'text-14'} w-full h-[42px] mb-[8px] text-[#718ebf]`}>{translate('total-member-payment')}</div>
                {
                  groupStatistical && groupStatistical.total_paid_member ?
                    <div className={`${isMobile ? 'text-12' : 'text-20'} font-700`}>{groupStatistical.total_paid_member}</div>
                    :
                    <div className={`${isMobile ? 'text-12' : 'text-20'} font-700`}>0</div>
                }
              </div>
            </div>

          </div>

          <div className={`${isMobile ? 'flex-wrap' : 'grid grid-cols-3'} gap-[12px] flex items-end mt-[16px]`}>
            {
              groups.length > 0 &&
              <div className={`${isMobile ? 'w-[calc(50%-6px)]' : 'w-full'}`}>
                <div className="text-14 font-500 mb-[8px]">{translate('group-name')}</div>

                <Select
                  styles={{
                    input: (base) => ({
                      ...base,
                      "input:focus": {
                        boxShadow: "none",
                      },
                    })
                  }}
                  defaultValue=""
                  name="tickets"
                  options={groups}
                  className="text-14"
                  classNamePrefix="select"
                  placeholder={groups[0].label}
                  onChange={(e: any) => setGroupName(e)} />
              </div>
            }

            <div className={`${isMobile ? 'w-[calc(50%-6px)]' : 'w-full'}`}>
              <div className="text-14 font-500 mb-[8px]">{translate('group-status')}</div>

              <Select
                styles={{
                  input: (base) => ({
                    ...base,
                    "input:focus": {
                      boxShadow: "none",
                    },
                  })
                }}
                defaultValue={groupStatus}
                name="group status"
                options={group_status_list}
                className="text-14"
                classNamePrefix="select"
                placeholder={group_status_list[0].label}
                onChange={(e: any) => setGroupStatus(e)} />
            </div>

            <div className={`${isMobile ? 'w-full' : 'w-[146px]'} h-[38px] rounded-[4px] border-[1px] border-[#A6A1FD] flex items-center justify-center cursor-pointer text-[#081774]`} onClick={handleExportGroup}>
              <img src="/images/icons/icon-download.svg" className="mr-[4px]" alt="icon-download" />

              <div className="text-[14px]">{translate('export-report')}</div>
            </div>
          </div>
        </div>
      }

      {
        (ticketTab === 0 && !isMobile) &&
        <div className="text-14 text-[#2b3033]">
          <table className="w-full min-w-max table-auto text-left">
            <thead>
              <tr className='bg-[#e9ecff] h-[53px]'>
                {TABLE_HEAD.map((head, i) => (
                  <th
                    key={head}
                  >
                    <Typography
                      variant="small"
                      className="text-14 !font-500 px-[8px]"
                      placeholder="Typography"
                      onPointerEnterCapture="none" onPointerLeaveCapture="none" onResize={() => { }}
                      onResizeCapture={() => { }}
                    >
                      {head}
                    </Typography>

                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {
                ticketsBoard.length > 0 &&
                ticketsBoard.map((item: any, index: number) => {

                  return (
                    <tr key={`event_item_${index}`} className="text-14 h-[53px] border-b border-[#e5e7eb]">
                      <td className="px-[8px]">
                        <Typography
                          variant="small"
                          placeholder="Typography"
                          className="table-value"
                          onPointerEnterCapture="none" onPointerLeaveCapture="none" onResize={() => { }}
                          onResizeCapture={() => { }}
                        >
                          {(index < 9) && pagingTickets.current_page - 1}{index + 1}
                        </Typography>
                      </td>

                      <td className="board-column">
                        <Typography
                          variant="small"
                          placeholder="Typography"
                          className="table-value"
                          onPointerEnterCapture="none" onPointerLeaveCapture="none" onResize={() => { }}
                          onResizeCapture={() => { }}
                        >
                          {get(item, `name_${language}`, '')}
                        </Typography>
                      </td>

                      {
                        (item.ticket_phase_name_vi && item.ticket_phase_name_vi !== '') ?
                          <td className="board-column">
                            <Typography
                              onPointerEnterCapture="none" onPointerLeaveCapture="none" onResize={() => { }}
                              onResizeCapture={() => { }}
                              variant="small"
                              className="table-value"
                              placeholder="Typography"
                            >
                              {get(item, `ticket_phase_name_${language}`, '')}
                            </Typography>
                          </td>
                          :
                          <td className="board-column">
                            <Typography
                              onPointerEnterCapture="none" onPointerLeaveCapture="none" onResize={() => { }}
                              onResizeCapture={() => { }}
                              variant="small"
                              className="table-value"
                              placeholder="Typography"
                            >
                              N/A
                            </Typography>
                          </td>
                      }

                      <td className="board-column">
                        <Typography
                          onPointerEnterCapture="none" onPointerLeaveCapture="none" onResize={() => { }}
                          onResizeCapture={() => { }}
                          variant="small"
                          className="table-value"
                          placeholder="Typography"
                        >
                          <LightTooltip title={`đ ${(+item.price > 0) ? item.price.toLocaleString('de-DE') : item.price}`} placement="top-start">
                            <span className="cursor-pointer">đ {shortenNumber(item.price)}</span>
                          </LightTooltip>
                        </Typography>
                      </td>

                      <td className="board-column">
                        <Typography
                          onPointerEnterCapture="none" onPointerLeaveCapture="none" onResize={() => { }}
                          onResizeCapture={() => { }}
                          variant="small"
                          className="table-value"
                          placeholder="Typography"
                        >
                          <LightTooltip title={`${(+item.quantity > 0) ? item.quantity.toLocaleString('de-DE') : item.quantity}`} placement="top-start">
                            <span className="cursor-pointer">{shortenNumber(item.quantity, 'number')}</span>
                          </LightTooltip>
                        </Typography>
                      </td>

                      <td className="board-column">
                        <Typography
                          onPointerEnterCapture="none" onPointerLeaveCapture="none" onResize={() => { }}
                          onResizeCapture={() => { }}
                          variant="small"
                          className="table-value"
                          placeholder="Typography"
                        >
                          <LightTooltip title={`${symbolCurrency} ${(+item.gross_revenue > 0) ? item.gross_revenue.toLocaleString('de-DE') : item.gross_revenue}`} placement="top-start">
                            <span className="cursor-pointer">{symbolCurrency} {shortenNumber(item.gross_revenue)}</span>
                          </LightTooltip>
                        </Typography>
                      </td>

                      <td className="board-column">
                        <Typography
                          onPointerEnterCapture="none" onPointerLeaveCapture="none" onResize={() => { }}
                          onResizeCapture={() => { }}
                          variant="small"
                          className="table-value"
                          placeholder="Typography"
                        >
                          <LightTooltip title={`${symbolCurrency} ${(+item.discount_amount > 0) ? item.discount_amount.toLocaleString('de-DE') : item.discount_amount}`} placement="top-start">
                            <span className="cursor-pointer">{symbolCurrency} {shortenNumber(item.discount_amount)}</span>
                          </LightTooltip>
                        </Typography>
                      </td>

                      <td className="board-column">
                        <Typography
                          onPointerEnterCapture="none" onPointerLeaveCapture="none" onResize={() => { }}
                          onResizeCapture={() => { }}
                          variant="small"
                          className="table-value"
                          placeholder="Typography"
                        >
                          <LightTooltip title={`${symbolCurrency} ${(+item.net_revenue > 0) ? item.net_revenue.toLocaleString('de-DE') : item.net_revenue}`} placement="top-start">
                            <span className="cursor-pointer">{symbolCurrency} {shortenNumber(item.net_revenue)}</span>
                          </LightTooltip>
                        </Typography>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>

          {
            pagingTickets &&
            <div className={`board__bottom ${isMobile ? 'mt-[16px]' : 'pagination-desktop'}`}>
              {
                pagingTickets.total_page > 1 &&
                <Pagination
                  setPage={setTicketsPage}
                  paging={pagingTickets}
                />
              }
            </div>
          }
        </div>
      }

      {
        (ticketTab === 0 && isMobile) &&
        <div className="w-full p-[16px] bg-[#f6f5fe] grid grid-cols-1 gap-[12px]">
          {
            ticketsBoard.length > 0 &&
            ticketsBoard.map((item: any, index: number) => {
              return (
                <div className="w-full bg-white rounded-[8px] p-[16px]" key={`event_item_${index}`}>
                  <div className="w-full flex items-start justify-between gap-x-[12px]">
                    <div className="w-[100px] text-14 font-500">{translate('no')}</div>
                    <div className="text-14 flex-1">{(index < 9) && pagingTickets.current_page - 1}{index + 1}</div>
                  </div>

                  <div className="w-full flex items-start justify-between gap-x-[12px]">
                    <div className="w-[100px] text-14 font-500">{translate('ticket-name')}</div>
                    <div className="text-14 flex-1">{get(item, `name_${language}`, '')}</div>
                  </div>

                  <div className="w-full flex items-start justify-between gap-x-[12px]">
                    <div className="w-[100px] text-14 font-500">{translate('ticket-phase')}</div>
                    {
                      (item.ticket_phase_name_vi && item.ticket_phase_name_vi !== '') ?
                        <div className="text-14 flex-1">{get(item, `ticket_phase_name_${language}`, '')}</div>
                        :
                        <div className="text-14 flex-1">N/A</div>
                    }
                  </div>

                  <div className="w-full flex items-start justify-between gap-x-[12px]">
                    <div className="w-[100px] text-14 font-500">{translate('price')}</div>
                    <div className="text-14 flex-1">đ {shortenNumber(item.price)}</div>
                  </div>

                  <div className="w-full flex items-start justify-between gap-x-[12px]">
                    <div className="w-[100px] text-14 font-500">{translate('quantity')}</div>
                    <div className="text-14 flex-1">{item.quantity}</div>
                  </div>

                  <div className="w-full flex items-start justify-between gap-x-[12px]">
                    <div className="w-[100px] text-14 font-500">{translate('gross-revenue-event')}</div>
                    <div className="text-14 flex-1">{symbolCurrency} {shortenNumber(item.gross_revenue)}</div>
                  </div>

                  <div className="w-full flex items-start justify-between gap-x-[12px]">
                    <div className="w-[100px] text-14 font-500">{translate('discounts')}</div>
                    <div className="text-14 flex-1">{symbolCurrency} {shortenNumber(item.discount_amount)}</div>
                  </div>

                  <div className="w-full flex items-start justify-between gap-x-[12px]">
                    <div className="w-[100px] text-14 font-500">{translate('net-revenue-event')}</div>
                    <div className="text-14 flex-1">{symbolCurrency} {shortenNumber(item.net_revenue)}</div>
                  </div>
                </div>
              )
            })
          }

          {
            pagingTickets &&
            <div className="board__bottom">
              {
                pagingTickets.total_page > 1 &&
                <Pagination
                  setPage={setTicketsPage}
                  paging={pagingTickets}
                />
              }
            </div>
          }
        </div>
      }

      {
        (ticketTab === 1 && !isMobile) &&
        <div className="w-full mt-[16px]">
          <div className="h-full w-full overflow-scroll overflow-y-hidden">
            <table className="w-full min-w-max text-left table-auto">
              <thead className='bg-[#e9ecff]'>
                <tr>
                  {TABLE_HEAD_GROUP.map((head, i) => (
                    <th
                      key={head}
                      className="p-4"
                    >
                      <Typography
                        variant="small"
                        className="text-14 !font-500"
                        placeholder="Typography"
                        onPointerEnterCapture="none" onPointerLeaveCapture="none" onResize={() => { }}
                        onResizeCapture={() => { }}
                      >
                        {head}

                        {
                          head === translate('status') &&
                          <LightTooltip title={<span dangerouslySetInnerHTML={{ __html: translate('tooltip-status-group') }} />} placement="left">
                            <img src="/images/icons/tooltip.svg" className="icon-tooltip" alt="icon-tooltip" />
                          </LightTooltip>
                        }
                      </Typography>

                    </th>
                  ))}
                </tr>
              </thead>

              {
                groupStatistical &&
                <tbody>
                  {
                    (groupStatistical.statistical_data && groupStatistical.statistical_data.length > 0) &&
                    groupStatistical.statistical_data.map((item: any, index: number) => {

                      return (
                        <tr key={`event_${item.code}_${index}`} className="table-row">
                          <td className="stt p-4">
                            <Typography
                              variant="small"
                              className="table-value"
                              placeholder="Typography"
                              onPointerEnterCapture="none" onPointerLeaveCapture="none" onResize={() => { }}
                              onResizeCapture={() => { }}
                            >
                              {index + 1}
                            </Typography>
                          </td>

                          <td className="p-4">
                            <Typography
                              variant="small"
                              className="table-value"
                              placeholder="Typography"
                              onPointerEnterCapture="none" onPointerLeaveCapture="none" onResize={() => { }}
                              onResizeCapture={() => { }}
                            >
                              {get(item, `name_${language}`, '')}
                            </Typography>
                          </td>

                          <td className="p-4">
                            <Typography
                              onPointerEnterCapture="none" onPointerLeaveCapture="none" onResize={() => { }}
                              onResizeCapture={() => { }}
                              variant="small"
                              className="table-value"
                              placeholder="Typography"
                            >
                              <LightTooltip title={`${item.attendant_quantity}`} placement="top-start">
                                <span className="cursor-pointer">{shortenNumber(item.attendant_quantity, 'number')}</span>
                              </LightTooltip>
                            </Typography>
                          </td>

                          <td className="p-4">
                            <Typography
                              onPointerEnterCapture="none" onPointerLeaveCapture="none" onResize={() => { }}
                              onResizeCapture={() => { }}
                              variant="small"
                              className="table-value"
                              placeholder="Typography"
                            >
                              <LightTooltip title={`${item.attendant_paid_quantity}`} placement="top-start">
                                <span className="cursor-pointer">{shortenNumber(item.attendant_paid_quantity, 'number')}</span>
                              </LightTooltip>
                            </Typography>
                          </td>

                          <td className="p-4">
                            <Typography
                              onPointerEnterCapture="none" onPointerLeaveCapture="none" onResize={() => { }}
                              onResizeCapture={() => { }}
                              variant="small"
                              className="table-value"
                              placeholder="Typography"
                            >
                              <LightTooltip title={`đ ${item.total_amount}`} placement="top-start">
                                <span className="cursor-pointer">đ {shortenNumber(item.total_amount)}</span>
                              </LightTooltip>
                            </Typography>
                          </td>

                          <td className="p-4">
                            <Typography
                              onPointerEnterCapture="none" onPointerLeaveCapture="none" onResize={() => { }}
                              onResizeCapture={() => { }}
                              variant="small"
                              className="table-value"
                              placeholder="Typography"
                            >
                              <LightTooltip title={`đ ${item.discount_amount}`} placement="top-start">
                                <span className="cursor-pointer">đ {shortenNumber(item.discount_amount)}</span>
                              </LightTooltip>
                            </Typography>
                          </td>

                          <td className="p-4">
                            <Typography
                              onPointerEnterCapture="none" onPointerLeaveCapture="none" onResize={() => { }}
                              onResizeCapture={() => { }}
                              variant="small"
                              className="table-value"
                              placeholder="Typography"
                            >
                              <LightTooltip title={`đ ${item.final_amount}`} placement="top-start">
                                <span className="cursor-pointer">đ {shortenNumber(item.final_amount)}</span>
                              </LightTooltip>
                            </Typography>
                          </td>

                          <td className="p-4">
                            <Typography
                              onPointerEnterCapture="none" onPointerLeaveCapture="none" onResize={() => { }}
                              onResizeCapture={() => { }}
                              variant="small"
                              className="table-value"
                              placeholder="Typography"
                            >
                              {moment(item.expired_date).format('DD/MM/YYYY HH:mm:ss')}
                            </Typography>
                          </td>

                          <td className="p-4 flex">
                            {/* <Typography
                              onPointerEnterCapture="none" onPointerLeaveCapture="none" onResize={() => { }}
                              onResizeCapture={() => { }}
                              variant="small"
                              className="table-value"
                              placeholder="Typography"
                            >
                              {
                                item.group_status === 0 &&
                                <span className="group-status closed">{translate('group-canceled')}</span>
                              }
                              {
                                item.group_status === 1 &&
                                <span className="group-status opening">{translate('group-opening')}</span>
                              }
                              {
                                item.group_status === 2 &&
                                <span className="group-status sending">{translate('group-sending')}</span>
                              }
                              {
                                item.group_status === 3 &&
                                <span className="group-status closed">{translate('group-closed')}</span>
                              }
                              {
                                item.group_status === 4 &&
                                <span className="group-status success">{translate('group-success')}</span>
                              }
                            </Typography> */}

                            {
                              item.group_status === 0 &&
                              <RenderStatusTag code={item.group_status} text={translate('group-canceled')} />
                            }
                            {
                              item.group_status === 1 &&
                              <RenderStatusTag code={item.group_status} text={translate('group-opening')} />
                            }
                            {
                              item.group_status === 2 &&
                              <RenderStatusTag code={item.group_status} text={translate('group-sending')} />
                            }
                            {
                              item.group_status === 3 &&
                              <RenderStatusTag code={item.group_status} text={translate('group-closed')} />
                            }
                            {
                              item.group_status === 4 &&
                              <RenderStatusTag code={item.group_status} text={translate('group-success')} />
                            }
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              }

            </table>
          </div>
        </div>
      }

      {
        (ticketTab === 1 && groupStatistical && isMobile) &&
        <div className="w-full p-[16px] bg-[#f6f5fe] grid grid-cols-1 gap-[12px]">
          {
            (groupStatistical.statistical_data && groupStatistical.statistical_data.length > 0) &&
            groupStatistical.statistical_data.map((item: any, index: number) => {
              return (
                <div className="w-full rounded-[8px] bg-white grid grid-cols-1 gap-[12px] p-[16px]" key={`group_statistical_item_${index}`}>
                  <div className="flex items-start justify-between text-14 gap-x-[8px]">
                    <div className="w-[100px] font-500">{translate('no')}</div>
                    <div className="flex-1">{index + 1}</div>
                  </div>

                  <div className="flex items-start justify-between text-14 gap-x-[8px]">
                    <div className="w-[100px] font-500">{translate('group-name')}</div>
                    <div className="flex-1">{get(item, `name_${language}`, '')}</div>
                  </div>

                  <div className="flex items-start justify-between text-14 gap-x-[8px]">
                    <div className="w-[100px] font-500">{translate('members')}</div>
                    <div className="flex-1">{item.attendant_quantity}</div>
                  </div>

                  <div className="flex items-start justify-between text-14 gap-x-[8px]">
                    <div className="w-[100px] font-500">{translate('paid')}</div>
                    <div className="flex-1">{item.attendant_paid_quantity}</div>
                  </div>

                  <div className="flex items-start justify-between text-14 gap-x-[8px]">
                    <div className="w-[100px] font-500">{translate('gross-revenue-event')}</div>
                    <div className="flex-1">đ {shortenNumber(item.total_amount)}</div>
                  </div>

                  <div className="flex items-start justify-between text-14 gap-x-[8px]">
                    <div className="w-[100px] font-500">{translate('discounts')}</div>
                    <div className="flex-1">đ {shortenNumber(item.discount_amount)}</div>
                  </div>

                  <div className="flex items-start justify-between text-14 gap-x-[8px]">
                    <div className="w-[100px] font-500">{translate('net-revenue-event')}</div>
                    <div className="flex-1">đ {shortenNumber(item.final_amount)}</div>
                  </div>

                  <div className="flex items-start justify-between text-14 gap-x-[8px]">
                    <div className="w-[100px] font-500">{translate('expired-date')}</div>
                    <div className="flex-1">{moment(item.expired_date).format('DD/MM/YYYY HH:mm:ss')}</div>
                  </div>

                  <div className="flex items-start justify-between text-14 gap-x-[8px]">
                    <div className="w-[100px] font-500 flex gap-x-[4px]">
                      <span>{translate('status')}</span>

                      <LightTooltip title={<span dangerouslySetInnerHTML={{ __html: translate('tooltip-status-group') }} />} placement="top-start">
                        <img src="/images/icons/tooltip.svg" className="ml-1" alt="icon-tooltip" />
                      </LightTooltip>
                    </div>
                    {/* <div className="flex-1">
                      {
                        item.group_status === 0 &&
                        <span className="group-status closed">{translate('group-canceled')}</span>
                      }
                      {
                        item.group_status === 1 &&
                        <span className="group-status opening">{translate('group-opening')}</span>
                      }
                      {
                        item.group_status === 2 &&
                        <span className="group-status sending">{translate('group-sending')}</span>
                      }
                      {
                        item.group_status === 3 &&
                        <span className="group-status closed">{translate('group-closed')}</span>
                      }
                      {
                        item.group_status === 4 &&
                        <span className="group-status success">{translate('group-success')}</span>
                      }
                    </div> */}

                    <div className='flex-1'>
                      <div className='flex'>
                        {
                          item.group_status === 0 &&
                          <RenderStatusTag code={item.group_status} text={translate('group-canceled')} />
                        }
                        {
                          item.group_status === 1 &&
                          <RenderStatusTag code={item.group_status} text={translate('group-opening')} />
                        }
                        {
                          item.group_status === 2 &&
                          <RenderStatusTag code={item.group_status} text={translate('group-sending')} />
                        }
                        {
                          item.group_status === 3 &&
                          <RenderStatusTag code={item.group_status} text={translate('group-closed')} />
                        }
                        {
                          item.group_status === 4 &&
                          <RenderStatusTag code={item.group_status} text={translate('group-success')} />
                        }
                      </div>
                    </div>
                  </div>
                </div>
              )
            })
          }
        </div>
      }

    </div>
  )
};

export default TicketGroup;